import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PersonalInfoForm from './components/PersonalInfoForm';
import KYCStatusPanel from './components/KYCStatusPanel';
import AccountLimitsSection from './components/AccountLimitsSection';
import DocumentManagement from './components/DocumentManagement';
import SecuritySettings from './components/SecuritySettings';
import NotificationsPanel from './components/NotificationsPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ProfileManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Mock user data
  const mockUser = {
    id: 'user_123',
    name: 'John Smith',
    email: 'john.smith@email.com',
    role: 'customer',
    firstName: 'John',
    lastName: 'Smith',
    phone: '+1 555-0123',
    dateOfBirth: '1985-06-15',
    nationality: 'US',
    address: '123 Main Street',
    city: 'New York',
    state: 'NY',
    postalCode: '10001',
    country: 'US',
    occupation: 'Software Engineer',
    sourceOfFunds: 'employment',
    kycStatus: {
      overall: 'pending',
      identity: 'completed',
      address: 'completed',
      selfie: 'pending',
      sourceOfFunds: 'not_started'
    },
    limits: {
      daily: 5000,
      monthly: 50000,
      annual: 500000
    },
    usage: {
      daily: 1250,
      monthly: 8750,
      annual: 45000
    },
    documents: {
      identity: {
        status: 'verified',
        uploadDate: '2024-11-15',
        expiryDate: '2029-06-15',
        fileName: 'passport_john_smith.pdf',
        fileSize: '2.4 MB',
        thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9f0ce84-1763467927621.png",
        thumbnailAlt: 'Scanned passport document showing photo page with personal information'
      },
      address: {
        status: 'verified',
        uploadDate: '2024-11-16',
        fileName: 'utility_bill_november_2024.pdf',
        fileSize: '1.8 MB',
        thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1acf01848-1763467927069.png",
        thumbnailAlt: 'Utility bill document showing address and billing information'
      },
      selfie: {
        status: 'pending',
        uploadDate: '2024-11-17',
        fileName: 'selfie_verification.jpg',
        fileSize: '3.2 MB',
        thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1239cd9e4-1763301201624.png",
        thumbnailAlt: 'Professional headshot of man with short brown hair in business attire for identity verification'
      }
    },
    securityData: {
      twoFactorEnabled: false,
      lastPasswordChange: '2024-11-10'
    }
  };

  useEffect(() => {
    // Simulate loading user data
    const loadUserData = async () => {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setUser(mockUser);
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, []);

  const handleLogout = () => {
    setUser(null);
    navigate('/login');
  };

  const handleSaveProfile = (updatedData) => {
    setUser((prev) => ({ ...prev, ...updatedData }));
    console.log('Profile updated:', updatedData);
  };

  const tabs = [
  { id: 'personal', label: 'Personal Info', icon: 'User', description: 'Basic information and contact details' },
  { id: 'kyc', label: 'KYC Status', icon: 'Shield', description: 'Verification status and requirements' },
  { id: 'limits', label: 'Account Limits', icon: 'CreditCard', description: 'Transfer limits and usage' },
  { id: 'documents', label: 'Documents', icon: 'FileText', description: 'Uploaded verification documents' },
  { id: 'security', label: 'Security', icon: 'Lock', description: 'Password and authentication settings' },
  { id: 'notifications', label: 'Notifications', icon: 'Bell', description: 'Account alerts and updates' }];


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Profile Management</h1>
              <p className="text-muted-foreground mt-2">
                Manage your personal information, security settings, and account preferences
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={16}>

                Back to Dashboard
              </Button>
              <Button
                onClick={() => navigate('/document-upload')}
                iconName="Upload"
                iconPosition="left"
                iconSize={16}>

                Upload Documents
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-lg p-4 sticky top-8">
              <h2 className="font-semibold text-foreground mb-4">Profile Sections</h2>
              <nav className="space-y-2">
                {tabs?.map((tab) =>
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`w-full flex items-start space-x-3 p-3 rounded-lg text-left transition-colors ${
                  activeTab === tab?.id ?
                  'text-primary bg-primary/10 border border-primary/20' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`
                  }>

                    <Icon name={tab?.icon} size={20} className="mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium">{tab?.label}</div>
                      <div className="text-xs opacity-75 mt-1">{tab?.description}</div>
                    </div>
                  </button>
                )}
              </nav>

              {/* Quick Stats */}
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="font-medium text-foreground mb-3">Quick Stats</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">KYC Status</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user?.kycStatus?.overall === 'completed' ?
                    'text-success bg-success/10' : 'text-warning bg-warning/10'}`
                    }>
                      {user?.kycStatus?.overall === 'completed' ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">2FA Status</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user?.securityData?.twoFactorEnabled ?
                    'text-success bg-success/10' : 'text-muted-foreground bg-muted'}`
                    }>
                      {user?.securityData?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Documents</span>
                    <span className="text-foreground font-medium">
                      {Object.values(user?.documents || {})?.filter((doc) => doc?.status === 'verified')?.length}/3
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="space-y-8">
              {activeTab === 'personal' &&
              <PersonalInfoForm
                user={user}
                onSave={handleSaveProfile} />

              }

              {activeTab === 'kyc' &&
              <KYCStatusPanel
                kycStatus={user?.kycStatus} />

              }

              {activeTab === 'limits' &&
              <AccountLimitsSection
                limits={user?.limits}
                usage={user?.usage} />

              }

              {activeTab === 'documents' &&
              <DocumentManagement
                documents={user?.documents} />

              }

              {activeTab === 'security' &&
              <SecuritySettings
                securityData={user?.securityData} />

              }

              {activeTab === 'notifications' &&
              <NotificationsPanel
                notifications={[]} />

              }
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default ProfileManagement;